'use client';

import { useMemo } from 'react';

import Grid2 from '@mui/material/Unstable_Grid2';

import Alert from '@mui/material/Alert';

import Typography from '@mui/material/Typography';

import { nanoid } from 'nanoid';

import Divider from '@mui/material/Divider';

import ChipTaskCheckStatus from '@/components/chip/ChipTaskCheckStatus';
import ChipTaskProcessTypeStatus from '@/components/chip/ChipTaskProcessTypeStatus';
import EntityDetailButton from '@/components/entity-detail/components/Button';
import EntityDetailContainer from '@/components/entity-detail/components/Container/EntityDetailContainer';
import SpinnerLoading from '@/components/SpinnerLoading';
import TextAction from '@/components/text-action/TextAction';
import ThumbsGallery from '@/components/thumbs-gallery';
import { DisplayField } from '@/components/typography/DisplayField';
import { useEntityImageStore } from '@/hooks/useEntityImage';
import type { TaskCheckResponse } from '@/types/apps/task-check.type';
import { EntityNameEnum } from '@/types/enums/entity-name.enum';
import { StatusTaskCheckEnum } from '@/types/enums/status-task-check.enum';
import { dateUtils } from '@/utils/date';
import { getExecutionModeLabel } from '@/utils/label-common/execution-mode';
import { t } from '@/utils/lang';
import { routeManager } from '@/utils/route';

interface TaskInfoProps {
    data: TaskCheckResponse | null;
}

const entityDetailIdentifier = `${routeManager.TASK_DETAIL}/${nanoid()}`;

const TaskInfo: React.FC<TaskInfoProps> = ({ data }) => {
    const executionLabel = getExecutionModeLabel(data?.executionMode);
    const idDone = data?.taskCheckStatus === StatusTaskCheckEnum.DONE;

    const { items: images, isLoading } = useEntityImageStore(
        EntityNameEnum.TaskCheck,
        data?.id || ''
    );

    const thumbs = useMemo(
        () => images.map(img => img.url).filter(Boolean),
        [images]
    );

    const valueReminderSchedule =
        data?.reminderScheduleTaskChecks?.length > 0
            ? data?.reminderScheduleTaskChecks[0]?.reminderSchedule?.name
            : undefined;

    let imageContent;

    if (isLoading) {
        imageContent = <SpinnerLoading />;
    } else if (thumbs.length > 0) {
        imageContent = <ThumbsGallery data={thumbs} />;
    } else {
        imageContent = (
            <Typography
                variant='caption'
                color='text.secondary'
                textAlign='center'
            >
                {t('Chưa có ảnh')}
            </Typography>
        );
    }

    const renderAlert = () => {
        if (data?.taskCheckStatus === StatusTaskCheckEnum.DECLINED) {
            return (
                <>
                    <Grid2 xs={12} mb={2}>
                        <Alert severity='error' sx={{ fontSize: 15 }}>
                            {t(
                                'KTV từ chối thực hiện Task. Vui lòng điều phối KTV khác.'
                            )}
                        </Alert>
                    </Grid2>
                    <DisplayField
                        xs={4}
                        label={
                            <TextAction value={data?.name} copy>
                                {t('KTV đề xuất')}
                            </TextAction>
                        }
                        value={
                            <EntityDetailButton
                                text={data?.reassigned?.fullname}
                                identifier={entityDetailIdentifier}
                                entityId={data?.reassigned?.id || ''}
                                type='ASSIGNEE'
                            />
                        }
                    />
                    <DisplayField
                        xs={8}
                        label={
                            <TextAction value={data?.name} copy>
                                {t('Lý do từ chối')}
                            </TextAction>
                        }
                        value={data?.reasonDeclined}
                    />
                    <Divider sx={{ width: '100%' }} />
                </>
            );
        }

        if (data?.isLateTaskCheck) {
            return (
                <Grid2 xs={12}>
                    <Alert severity='error' sx={{ fontSize: 15 }}>
                        {t(
                            'Quá hạn xử lý. Vui lòng kiểm tra lại tiến độ của kỹ thuật viên!'
                        )}
                    </Alert>
                </Grid2>
            );
        }

        return null;
    };

    return (
        <EntityDetailContainer identifier={entityDetailIdentifier}>
            <Grid2 container spacing={4}>
                {renderAlert()}
                {idDone && (
                    <Grid2 xs={4} alignSelf='center'>
                        {imageContent}
                    </Grid2>
                )}
                <Grid2 xs={idDone ? 8 : 12}>
                    <Grid2 container spacing={5}>
                        <DisplayField
                            label={
                                <TextAction value={data?.name} copy>
                                    {t('Tên Task')}
                                </TextAction>
                            }
                            value={data?.name}
                        />
                        <DisplayField
                            label={
                                <TextAction
                                    value={data?.taskCheckType?.name}
                                    copy
                                >
                                    {t('Loại Task')}
                                </TextAction>
                            }
                            value={data?.taskCheckType?.name}
                        />
                        <DisplayField
                            label={
                                <TextAction value={data?.code} copy>
                                    {t('Mã Task')}
                                </TextAction>
                            }
                            value={data?.code}
                        />
                        <DisplayField
                            label={
                                <TextAction
                                    value={data?.device?.serialNumber}
                                    copy
                                >
                                    {t('Seri')}
                                </TextAction>
                            }
                            value={data?.device?.serialNumber}
                        />
                        <DisplayField
                            label={
                                <TextAction value={data?.device?.name} copy>
                                    {t('Tên thiết bị')}
                                </TextAction>
                            }
                            value={
                                <EntityDetailButton
                                    text={data?.device?.name}
                                    identifier={entityDetailIdentifier}
                                    entityId={data?.device?.id || ''}
                                    type='DEVICE'
                                />
                            }
                        />
                        <DisplayField
                            label={
                                <TextAction
                                    value={data?.ticket?.customer?.name}
                                    copy
                                >
                                    {t('Khách hàng')}
                                </TextAction>
                            }
                            value={
                                <EntityDetailButton
                                    text={data?.ticket?.customer?.name}
                                    identifier={entityDetailIdentifier}
                                    entityId={data?.ticket?.customer?.id || ''}
                                    type='CUSTOMER'
                                />
                            }
                        />
                        <DisplayField
                            label={
                                <TextAction value={data?.ticketCode} copy>
                                    {t('Mã Ticket')}
                                </TextAction>
                            }
                            value={
                                <EntityDetailButton
                                    text={data?.ticketCode}
                                    identifier={entityDetailIdentifier}
                                    entityId={data?.ticket?.id || ''}
                                    type='TICKET'
                                />
                            }
                        />
                        <DisplayField
                            label={
                                <TextAction value={executionLabel} copy>
                                    {t('Hình thức thực hiện')}
                                </TextAction>
                            }
                            value={executionLabel}
                        />
                        <DisplayField
                            label='Loại Task xử lý'
                            value={
                                <ChipTaskProcessTypeStatus
                                    status={data?.taskProcessType ?? 'default'}
                                />
                            }
                        />
                        <DisplayField
                            label={
                                <TextAction
                                    value={
                                        data?.scheduledTime &&
                                        dateUtils.getDate(data?.scheduledTime)
                                    }
                                    copy
                                >
                                    {t('Ngày bắt đầu')}
                                </TextAction>
                            }
                            value={
                                data?.scheduledTime &&
                                dateUtils.getDate(data?.scheduledTime)
                            }
                        />
                        <DisplayField
                            label={
                                <TextAction value={data?.assigneeName} copy>
                                    {t('Người thực hiện')}
                                </TextAction>
                            }
                            value={
                                <EntityDetailButton
                                    text={data?.assigneeName || '-:-'}
                                    identifier={entityDetailIdentifier}
                                    entityId={data?.assigneeId || ''}
                                    type='ASSIGNEE'
                                />
                            }
                        />
                        <DisplayField
                            label={
                                <TextAction
                                    value={data?.taskCheckStatus}
                                    copy
                                    className='flex inline items-center'
                                >
                                    {t('Trạng thái', 'TaskDetail')}
                                </TextAction>
                            }
                            value={
                                <ChipTaskCheckStatus
                                    status={data?.taskCheckStatus ?? 'default'}
                                />
                            }
                        />
                        <DisplayField
                            label={
                                <TextAction
                                    value={data?.templateCheck?.name}
                                    copy
                                >
                                    {t('Loại phiếu')}
                                </TextAction>
                            }
                            value={data?.templateCheck?.name}
                        />
                        <DisplayField
                            label={
                                <TextAction
                                    value={
                                        data?.createdDate &&
                                        dateUtils.getDate(data?.createdDate)
                                    }
                                    copy
                                >
                                    {t('Ngày tạo')}
                                </TextAction>
                            }
                            value={
                                data?.createdDate &&
                                dateUtils.getDate(data?.createdDate)
                            }
                        />
                        {data?.taskCheckStatus && data?.businessTripForm && (
                            <>
                                <DisplayField
                                    label={
                                        <TextAction
                                            value={
                                                data?.businessTripForm &&
                                                dateUtils.getDateTimeLocal(
                                                    data?.businessTripForm
                                                        ?.checkinPlan
                                                )
                                            }
                                            copy
                                        >
                                            {t('Thời gian check-in (Dự kiến)')}
                                        </TextAction>
                                    }
                                    value={
                                        data?.businessTripForm &&
                                        dateUtils.getDateTimeLocal(
                                            data?.businessTripForm?.checkinPlan
                                        )
                                    }
                                />
                                <DisplayField
                                    label={
                                        <TextAction
                                            value={
                                                data?.businessTripForm &&
                                                dateUtils.getDateTimeLocal(
                                                    data?.businessTripForm
                                                        ?.checkoutPlan
                                                )
                                            }
                                            copy
                                        >
                                            {t('Thời gian check-out (Dự kiến)')}
                                        </TextAction>
                                    }
                                    value={
                                        data?.businessTripForm &&
                                        dateUtils.getDateTimeLocal(
                                            data?.businessTripForm?.checkoutPlan
                                        )
                                    }
                                />
                            </>
                        )}

                        {data?.taskCheckStatus &&
                            data?.businessTripForm?.checkin && (
                                <DisplayField
                                    label='Thời gian Check In (Thực tế)'
                                    value={
                                        data?.businessTripForm &&
                                        dateUtils.getDateTimeLocal(
                                            data?.businessTripForm?.checkin
                                        )
                                    }
                                />
                            )}
                        {data?.taskCheckStatus &&
                            data?.businessTripForm?.checkout && (
                                <DisplayField
                                    label='Thời gian Check Out (Thực tế)'
                                    value={
                                        data?.businessTripForm &&
                                        dateUtils.getDateTimeLocal(
                                            data?.businessTripForm?.checkout
                                        )
                                    }
                                />
                            )}
                        {data?.taskCheckStatus &&
                            data?.businessTripForm?.isCorrectLocation && (
                                <DisplayField
                                    label='Trạng thái vị trí'
                                    value={
                                        data?.businessTripForm
                                            ?.isCorrectLocation
                                            ? t('Đúng vị trí')
                                            : t('Sai vị trí')
                                    }
                                />
                            )}
                        <DisplayField
                            label={
                                <TextAction
                                    value={
                                        data?.isLateTaskCheck
                                            ? t('Trễ hạn')
                                            : t('Đúng hạn')
                                    }
                                    copy
                                >
                                    {t('Thời hạn')}
                                </TextAction>
                            }
                            value={
                                data?.isLateTaskCheck
                                    ? t('Trễ hạn')
                                    : t('Đúng hạn')
                            }
                        />
                        <DisplayField
                            label={
                                <TextAction value={valueReminderSchedule} copy>
                                    {t('Lịch bảo trì')}
                                </TextAction>
                            }
                            value={
                                <EntityDetailButton
                                    text={valueReminderSchedule}
                                    identifier={entityDetailIdentifier}
                                    entityId={data?.id || ''}
                                    type='REMINDER_SCHEDULE'
                                />
                            }
                        />
                        {data?.taskCheckStatus ===
                            StatusTaskCheckEnum.CANCEL && (
                            <DisplayField
                                label='Lý do hủy'
                                value={data?.reasonCancel}
                            />
                        )}
                        {data?.taskCheckStatus === StatusTaskCheckEnum.DONE && (
                            <DisplayField
                                label='Kết luận Task'
                                value={data?.taskSummary}
                            />
                        )}
                        {data?.taskEndTime && (
                            <DisplayField
                                label={
                                    <TextAction
                                        value={
                                            data?.taskEndTime &&
                                            dateUtils.getDateTime(
                                                data?.taskEndTime
                                            )
                                        }
                                        copy
                                    >
                                        {t('Thời gian hoàn thành')}
                                    </TextAction>
                                }
                                value={
                                    data?.taskEndTime &&
                                    dateUtils.getDateTime(
                                        data?.taskEndTime,
                                        dateUtils.defaultDatetimeFormatWithoutSeconds
                                    )
                                }
                            />
                        )}
                        <DisplayField label='Ghi chú' value={data?.note} />
                    </Grid2>
                </Grid2>
            </Grid2>
        </EntityDetailContainer>
    );
};

export default TaskInfo;
